import { cn } from "@utils/tw"
import { useCheckout } from "./useCheckout"
import { Dialog } from "radix-ui"
import { CheckoutStep, CheckoutStepKey, checkoutStore } from "./checkout-store"
import { useEffect, useMemo, useRef } from "react"
import { useCheckoutConfig } from "./useCheckoutConfig"
import { SelectTicketsCheckoutStep } from "./Checkout.SelectTickets"
import { SelectSeatsCheckoutStep } from "./Checkout.SelectSeats"
import { check } from "zod"
import { PaymentCheckoutStep } from "./Checkout.Payment"
import { getSpektrixCheckoutReturn } from "./spektrix-checkout"

export function Checkout() {
  const checkout = useCheckout()
  const config = useCheckoutConfig()
  const baseUrl = config.data?.webApiBaseUrl
  const checkoutReturnHandled = useRef(false)

  useEffect(() => {
    if (baseUrl) void checkoutStore.loadBasket(baseUrl)
  }, [baseUrl])

  useEffect(() => {
    if (!baseUrl || checkoutReturnHandled.current) return
    if (getSpektrixCheckoutReturn(baseUrl, window.location.search)) {
      checkoutReturnHandled.current = true
      checkoutStore.openCheckout()
    }
  }, [baseUrl])

  const contents = useMemo(() => {
    if (checkout.step === "tickets") {
      return <SelectTicketsCheckoutStep key={checkout.ticketSelectionRequest?.selectedSession?.sessionId} />
    } else if (checkout.step === "seats") {
      return <SelectSeatsCheckoutStep />
    } else if (checkout.step === "payment") {
      return <PaymentCheckoutStep />
    }
  }, [checkout.step, checkout.ticketSelectionRequest?.selectedSession?.sessionId])

  return (
    <Dialog.Root
      open={checkout.isOpen}
      onOpenChange={(open) => {
        if (!open) {
          checkout.close()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-blue/50 z-[60]" />
        <Dialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 max-h-[85vh] w-[90vw] max-w-[950px] -translate-x-1/2 -translate-y-1/2 !outline-none z-[61] bg-white border border-blue flex flex-col",
            checkout.step === "payment" && "h-[85vh]",
          )}
        >
          {/* Tabs */}
          <div className="border-b border-blue flex">
            <StepTabs
              steps={checkout.steps}
              activeStep={checkout.step}
              onSelect={checkout.goToStep}
              canAccessStep={(step) => {
                if (
                  checkout.steps.findIndex((item) => item.key === step) <
                  checkout.steps.findIndex((item) => item.key === checkout.step)
                ) {
                  return true
                }
                return step === checkout.step
              }}
            />
            <Dialog.Close asChild>
              <button className="size-8 flex-none inline-flex items-center justify-center text-blue" aria-label="Close">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M6.39916 18.3079L5.69141 17.6001L11.2914 12.0001L5.69141 6.40013L6.39916 5.69238L11.9992 11.2924L17.5992 5.69238L18.3069 6.40013L12.7069 12.0001L18.3069 17.6001L17.5992 18.3079L11.9992 12.7079L6.39916 18.3079Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </Dialog.Close>
          </div>

          {/* Contents */}
          {contents}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function StepTabs(props: {
  steps: CheckoutStep[]
  onSelect: (step: CheckoutStepKey) => void
  activeStep: CheckoutStepKey
  canAccessStep: (step: CheckoutStepKey) => boolean
}) {
  const isSingleStep = props.steps.length === 1

  return (
    <div className="flex grow">
      {props.steps.map((step, i) => {
        const enabled = props.canAccessStep(step.key)
        const active = props.activeStep === step.key
        return (
          <button
            key={step.key}
            onClick={(e) => {
              props.onSelect(step.key)
            }}
            className={cn(
              "type-label-s border-r text-blue border-blue px-2 text-center grow cursor-pointer",
              "disabled:text-blue/50 disabled:cursor-default",
              active && !isSingleStep && "bg-blue text-white",
              active && "cursor-default",
            )}
            disabled={!enabled}
          >
            {isSingleStep ? step.label : `${i + 1}. ${step.label}`}
          </button>
        )
      })}
    </div>
  )
}
